import React, { useEffect, useState, Fragment, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Grid } from '@mui/material';

import { Button, Select } from "Components";
import ModalComponent, { ModalBody, ModalFooter } from 'Components/Modal';
import { NotificationManager } from 'Components/Notifications';

import colors from 'Assets/sass/core/vars.scss';

import {
    fleetModalBulkOutputsOpen,
    fleetModalBulkOutputsSendCommand,
    fleetModalBulkOutputsGetOutputs,
    clearRedux
} from 'Redux/actions'

const FleetModalBulkOutputs = (props) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();

    const open = useSelector(state => state.fleetModalBulkOutputsRedux.open);
    const loading = useSelector(state => state.fleetModalBulkOutputsRedux.loading);
    const response = useSelector(state => state.fleetModalBulkOutputsRedux.response);
    const units = useSelector(state => state.fleetModalBulkOutputsRedux.units);

    const outputs = useSelector(state => state.fleetModalBulkOutputsRedux.outputs);
    const loadingOutputs = useSelector(state => state.fleetModalBulkOutputsRedux.loadingOutputs);

    const [outputsOptions, setOutputsOptions] = useState([]);
    const [availableActions, setAvailableActions] = useState([]);
    const [output, setOutput] = useState(null);
    const [unitsToCommand, setUnitsToCommand] = useState([]);

    useEffect(() => {
        return () => {
            dispatch(clearRedux("FLEET_MODAL_BULK_OUTPUTS"));
            setOutputsOptions([]);
            setOutput(null);
            setUnitsToCommand([]);
            setAvailableActions([]);
        }
    }, []);

    useEffect(() => {
        if (open && units?.length) {
            dispatch(fleetModalBulkOutputsGetOutputs(units));
            let outputsCount = 0;
            units.forEach(unit => {
                if (unit?.supportedOutputs > outputsCount) {
                    outputsCount = unit?.supportedOutputs;
                }
            });

            let i = 0;
            let options = [];
            while (i < outputsCount) {
                options.push({
                    id: i + 1,
                    name: `Output ${i + 1}`,
                });
                i++;
            }

            setOutputsOptions(options);
        }
    }, [units, open]);

    useEffect(() => {
        if (open && outputs) {
            if (outputs?.error) {
                NotificationManager.error(
                    messages['errorMessage'],
                    messages['error'],
                    3000,
                    null,
                    null,
                    'warning',
                    null
                );
    
                dispatch(clearRedux("FLEET_MODAL_BULK_OUTPUTS", { outputs: [] }));
            } else {
                if (outputsOptions?.length) {
                    setOutput(outputsOptions[0]);
                    validateAvailableActions(outputsOptions[0]);
                }
            }
        }
    }, [outputs, open]);

    useEffect(() => {
        if (open && response) {
            if (response?.error) {
                NotificationManager.error(
                    messages['errorMessage'],
                    messages['error'],
                    3000,
                    null,
                    null,
                    'warning',
                    null
                );
            } else {
                NotificationManager.success(
                    messages["fleetModalBulkOutputs_commandSendSuccess"],
                    messages["success"],
                    3000,
                    null,
                    null,
                    "warning",
                    null
                );
            }

            dispatch(clearRedux("FLEET_MODAL_BULK_OUTPUTS", { response: null }));
        }

    }, [response, open]);

    const validateAvailableActions = useCallback((value) => {
        if (units && units?.length) {
            let newAvailableActions = [];
            let newUnitsToCommand = [];
            units.forEach(unit => {
                let ot = getOutput(unit, value);
                if (ot) {
                    let action = `${ot?.type}`;
                    newUnitsToCommand.push({
                        unitEsn: unit?.esn,
                        unitType: unit?.type,
                        unitId: unit?.id,
                        command: `${ot?.type} ${ot?.state}`
                    });

                    if (!newAvailableActions?.includes(action)) {
                        newAvailableActions.push(action);
                    }
                }
            });

            setUnitsToCommand(newUnitsToCommand);
            setAvailableActions(newAvailableActions);
        }
    });

    const getOutput = useCallback((unit, v) => {
        return outputs[`output_${unit?.id}`]?.outputs?.find(o => o?.position == v?.id);
    });

    const handleClose = useCallback(() => {
        dispatch(fleetModalBulkOutputsOpen(false));
        dispatch(clearRedux("FLEET_MODAL_BULK_OUTPUTS"));
        setOutputsOptions([]);
        setOutput(null);
        setUnitsToCommand([]);
        setAvailableActions([]);
    });

    const title = messages['fleetModalBulkOutputs_title']?.replace('{number_units}', units?.length);

    return (
        <ModalComponent
            open={open}
            handleClose={handleClose}
            loading={loading || loadingOutputs}
            title={title}
            position="right"
            size="medium"
            style={{ overflow: 'hidden' }}
            className="overflow-hidden"
        >
            <ModalBody
                style={{
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    marginBottom: 0
                }}
            >
                <div
                    style={{
                        padding: "0px 24px 70px 24px",
                        height: "calc(100vh - 225px)",
                        overflowX: 'auto'
                    }}
                >
                    <div style={{ paddingTop: 10 }}>
                        <Select
                            id={"output"}
                            label={messages["fleetModalBulkOutputs_selectOutput"]}
                            placeholder={messages["select"]}
                            options={outputsOptions}
                            textField="name"
                            valueField="id"
                            value={output}
                            onChange={(value) => {
                                setOutput(value);
                                validateAvailableActions(value);
                            }}
                            required={true}
                            disabled={!outputsOptions?.length}
                            requiredMessage={messages["required_field"]}
                        />
                    </div>

                    <div style={{ padding: '20px 0px 10px 0px' }}>
                        <span>
                            {messages['fleetModalBulkOutputs_selectedUnits']}
                        </span>
                    </div>

                    <div>
                        {
                            units && units.map((unit, index) => {
                                let ot = getOutput(unit, output);

                                return (
                                    <Grid
                                        container
                                        direction="row"
                                        wrap="nowrap"
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                        key={index}
                                        style={{
                                            margin: "10px 0px 10px 0px",
                                            minWidth: 'max-content',
                                            width: '100%'
                                        }}
                                    >
                                        <Grid item style={{ flex: '0 0 auto', minWidth: 'max-content', paddingRight: 24 }}>
                                            <div className='very-strong' style={{ textDecoration: ot ? "auto" : "line-through", color: ot ? "black" : colors?.color_disabled_gray, whiteSpace: 'nowrap' }}>
                                                {unit?.label}
                                            </div>
                                            <div className='strong secondary-text' style={{ whiteSpace: 'nowrap' }}>
                                                {ot ? ot?.name : ""}
                                            </div>
                                        </Grid>

                                        <Grid item className='text-right' style={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}>
                                            <span className="strong secondary-text" style={{ color: colors?.color_disabled_gray, whiteSpace: 'nowrap' }}>
                                                {ot ? `${ot?.type} ${ot?.state}` : messages['fleetModalBulkOutputs_notSupported']}
                                            </span>
                                        </Grid>
                                    </Grid>
                                )
                            })
                        }
                    </div>
                </div>
            </ModalBody>

            <ModalFooter className="modalBodyWithShadow">
                <Fragment>
                    <Button
                        type="button"
                        onClick={handleClose}
                        className="btn-cancel"
                        disabled={loading || loadingOutputs}
                    >
                        {messages['cancel']}
                    </Button>

                    {
                        availableActions.includes("Toggle") &&
                        <Button
                            onClick={() => {
                                let newUnits = unitsToCommand?.filter(u => (u.command === "Toggle Off" || u.command === "Toggle On"));
                                dispatch(fleetModalBulkOutputsSendCommand({
                                    via: "SMS",
                                    message: {
                                        cannedCommand: {
                                            commandKey: `output${output?.id}`,
                                            action: "enable"
                                        }
                                    },
                                    description: `Toogle On output ${output?.id}`,
                                    to: newUnits.map(nu => {
                                        return { entityName: "Unit", entityId: nu?.unitId }
                                    })
                                }));
                            }}
                            className="btn-blue"
                            disabled={loading || loadingOutputs}
                        >
                            {messages['fleet_send_on']}
                        </Button>
                    }

                    {
                        availableActions.includes("Toggle") &&
                        <Button
                            onClick={() => {
                                let newUnits = unitsToCommand?.filter(u => (u.command === "Toggle Off" || u.command === "Toggle On"));
                                dispatch(fleetModalBulkOutputsSendCommand({
                                    via: "SMS",
                                    message: {
                                        cannedCommand: {
                                            commandKey: `output${output?.id}`,
                                            action: "disable"
                                        }
                                    },
                                    description: `Toogle Off output ${output?.id}`,
                                    to: newUnits.map(nu => {
                                        return { entityName: "Unit", entityId: nu?.unitId }
                                    })
                                }));
                            }}
                            className="btn-blue"
                            disabled={loading || loadingOutputs}
                        >
                            {messages['fleet_send_off']}
                        </Button>
                    }

                    {
                        (availableActions.includes("Pulse") || availableActions.includes("Pulse")) &&
                        <Button
                            onClick={() => {
                                let newUnits = unitsToCommand?.filter(u => u.command === "Pulse On" || u.command === "Pulse Off");
                                dispatch(fleetModalBulkOutputsSendCommand({
                                    via: "SMS",
                                    message: {
                                        cannedCommand: {
                                            commandKey: `output${output?.id}`,
                                            action: "pulse"
                                        }
                                    },
                                    description: `Pulse output ${output?.id}`,
                                    to: newUnits.map(nu => {
                                        return { entityName: "Unit", entityId: nu?.unitId }
                                    })
                                }));
                            }}
                            className="btn-blue"
                            disabled={loading || loadingOutputs}
                        >
                            {messages['fleet_send_pulse']}
                        </Button>
                    }

                    {
                    (availableActions.includes("singlePulse") || availableActions.includes("singlePulse")) &&
                        <Button
                            onClick={() => {
                                let newUnits = unitsToCommand?.filter(u => u.command.includes("singlePulse"));
                                dispatch(fleetModalBulkOutputsSendCommand({
                                    via: "SMS",
                                    message: {
                                        cannedCommand: {
                                            commandKey: `output${output?.id}`,
                                            action: "singlePulse"
                                        }
                                    },
                                    description: `Single Pulse output ${output?.id}`,
                                    to: newUnits.map(nu => {
                                        return { entityName: "Unit", entityId: nu?.unitId }
                                    })
                                }));
                            }}
                            className="btn-blue"
                            disabled={loading || loadingOutputs}
                        >
                            {messages['fleet_send_single_pulse']} 
                        </Button>
                    }
                </Fragment>
            </ModalFooter>
        </ModalComponent >
    );
};

export default FleetModalBulkOutputs;